import { Observable, of, throwError } from "rxjs";
import { map, shareReplay, tap } from "rxjs/operators";
import { inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { AuthService } from "@auth0/auth0-angular";
import { ENVIRONMENT_TOKEN } from "../types/environment.token";
import { Group } from "../types/group";
import { Protocol } from "../types/protocol";
import { MongoClientService } from "./mongo-client.service";
import { PatientProfile } from "../types/profile";
import {
  individualWebinarHolder,
  patientOfWebinarHolder,
} from "../functions/individual-course";

export interface CourseLiveGroupSummary {
  title: string;
  memberCount: number;
  therapistName: string;
  isIndividual?: boolean;
  patientName?: string;
}

export interface SessionAutomationConfig {
  canUnlockNextSession: boolean;
  isTimeBasedAutomationEnabled: boolean;
  scheduledUnlockDate?: string | null;
  scheduledLockDate?: string | null;
  unlockSmsMessage?: string | null;
  lockSmsMessage?: string | null;
}

export interface SessionOriginalState {
  can_unlock_next_session_on_completion?: boolean;
  is_time_based_automation_enabled?: boolean;
  scheduled_unlock_date?: string;
  scheduled_lock_date?: string;
  unlock_sms_message?: string;
  lock_sms_message?: string;
  is_unlocked?: boolean;
  is_manually_toggled?: boolean;
}

export interface SessionLockUpdate {
  isUnlocked: boolean;
  isManuallyToggled: boolean;
}

export interface BulkCompletionUpdate {
  enabled: boolean;
}

export enum SessionAutomationErrorType {
  NETWORK_ERROR = "NETWORK_ERROR",
  UNAUTHORIZED = "UNAUTHORIZED",
  SESSION_NOT_FOUND = "SESSION_NOT_FOUND",
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
}

export interface SessionAutomationError {
  type: SessionAutomationErrorType;
  message: string;
  details?: any;
  userMessage: string;
  // Transloco key for userMessage. Apps with Transloco providers should render
  // this; uplift-admin has none and falls back to the English userMessage.
  userMessageKey: string;
}

export function createSessionAutomationError(
  error: any,
  operation: string,
): SessionAutomationError {
  if (error?.status === 401) {
    return {
      type: SessionAutomationErrorType.UNAUTHORIZED,
      // i18n-ignore — developer-facing, paired with userMessage
      message: "Unauthorized access",
      details: error,
      // i18n-ignore — English fallback for uplift-admin, which has no Transloco
      userMessage:
        "You are not authorized to perform this action. Please log in again.",
      userMessageKey: "sdkBlocks.sessionError.unauthorized",
    };
  }

  if (error?.status === 404) {
    return {
      type: SessionAutomationErrorType.SESSION_NOT_FOUND,
      // i18n-ignore — developer-facing, paired with userMessage
      message: "Session not found",
      details: error,
      // i18n-ignore — English fallback for uplift-admin, which has no Transloco
      userMessage:
        "The session could not be found. Please refresh the page and try again.",
      userMessageKey: "sdkBlocks.sessionError.notFound",
    };
  }

  if (error?.status >= 500) {
    return {
      type: SessionAutomationErrorType.NETWORK_ERROR,
      // i18n-ignore — developer-facing, paired with userMessage
      message: "Server error",
      details: error,
      // i18n-ignore — English fallback for uplift-admin, which has no Transloco
      userMessage: "A server error occurred. Please try again later.",
      userMessageKey: "sdkBlocks.sessionError.server",
    };
  }

  return {
    type: SessionAutomationErrorType.UNKNOWN_ERROR,
    // i18n-ignore — developer-facing, paired with userMessage
    message: `Unknown error during ${operation}`,
    details: error,
    // i18n-ignore — English fallback for uplift-admin, which has no Transloco
    userMessage: "An unexpected error occurred. Please try again.",
    userMessageKey: "sdkBlocks.sessionError.unknown",
  };
}

/**
 * Raised when a session lock/unlock write matched 0 documents. The request itself
 * succeeded — nothing matched the filter (e.g. the session is gone or the view is
 * stale). Callers should surface this as a warning ("no update matched"), not a hard
 * error. Use isSessionUpdateNoMatch() to distinguish it from real failures.
 */
export class SessionUpdateNoMatchError extends Error {
  readonly code = "SESSION_UPDATE_NO_MATCH";
  constructor(public readonly action: string) {
    super(`${action}: no matching session was found, so nothing was changed.`);
    this.name = "SessionUpdateNoMatchError";
  }
}

export function isSessionUpdateNoMatch(
  error: unknown,
): error is SessionUpdateNoMatchError {
  return (
    error instanceof SessionUpdateNoMatchError ||
    (typeof error === "object" &&
      error !== null &&
      (error as { code?: string }).code === "SESSION_UPDATE_NO_MATCH")
  );
}

@Injectable({
  providedIn: "root",
})
export class GroupService {
  private _auth = inject(AuthService);
  private _env = inject(ENVIRONMENT_TOKEN);
  private _http = inject(HttpClient);
  private _mongo = inject(MongoClientService);
  private _currentUserId: string | null = null;
  private _groupCache = new Map<string, Observable<Group>>();
  private _groupsByCreatorCache = new Map<string, Observable<Group[]>>();

  constructor() {
    this._auth.user$.subscribe((user) => {
      this._currentUserId = user?.sub || null;
    });
  }

  getGroupList(): Observable<Group[]> {
    return this._mongo.find<Group>(
      this._env.groupsListDB,
      this._env.groupListCollection,
      {},
      {
        projection: {
          "course.session_list.session_module_item_list": 0,
          "course.course_menu_item_list": 0,
          "course.protocol_list": 0,
          "course.description": 0,
          protocol: 0,
        },
      },
    );
  }

  updateProtocolsInAllGroups(protocol: Protocol): Observable<any> {
    return this._mongo.updateMany(
      this._env.groupsListDB,
      this._env.groupListCollection,
      { "protocol._id": protocol._id },
      { $set: { protocol: { ...protocol } } },
    );
  }

  getLiveGroupsUsingCourse(
    courseId: string,
  ): Observable<CourseLiveGroupSummary[]> {
    const pipeline = [
      {
        $match: {
          $expr: {
            $and: [
              {
                $or: [
                  { $eq: [{ $toString: "$course._id" }, courseId] },
                  { $eq: ["$protocol_id", courseId] },
                ],
              },
              { $ne: ["$is_archived", true] },
            ],
          },
        },
      },
      {
        $lookup: {
          from: "profiles",
          localField: "creator_user_id",
          foreignField: "owner_user_id",
          as: "creator_profile",
        },
      },
      {
        $project: {
          title: 1,
          member_count: {
            $size: { $ifNull: ["$member_profile_list", []] },
          },
          therapist_first: {
            $ifNull: [{ $arrayElemAt: ["$creator_profile.first_name", 0] }, ""],
          },
          therapist_last: {
            $ifNull: [{ $arrayElemAt: ["$creator_profile.last_name", 0] }, ""],
          },
        },
      },
      { $sort: { member_count: -1 } },
    ];

    return this._mongo
      .aggregate<{
        title: string;
        member_count: number;
        therapist_first: string;
        therapist_last: string;
      }>(this._env.groupsListDB, "groups_live", pipeline)
      .pipe(
        map((rows) =>
          (rows || []).map((row) => ({
            title: row.title || "Untitled group",
            memberCount: row.member_count || 0,
            therapistName:
              `${row.therapist_first || ""} ${row.therapist_last || ""}`.trim(),
          })),
        ),
      );
  }

  getGroupByTherapistList(therapistID: string): Observable<Group[]> {
    return this._mongo.find<Group>(
      this._env.groupsListDB,
      this._env.groupListCollection,
      { creator_user_id: therapistID },
    );
  }

  getGroupsByCreatorIds(creatorIds: string[]): Observable<Group[]> {
    const cacheKey = [...creatorIds].sort().join(",");
    if (!this._groupsByCreatorCache.has(cacheKey)) {
      this._groupsByCreatorCache.set(
        cacheKey,
        this._mongo
          .find<Group>(this._env.groupsListDB, this._env.groupListCollection, {
            creator_user_id: { $in: creatorIds },
            is_archived: { $ne: true },
          })
          .pipe(shareReplay(1)),
      );
    }
    return this._groupsByCreatorCache.get(cacheKey)!;
  }

  getActiveGroupsByIds(groupIds: string[]): Observable<Group[]> {
    if (!groupIds.length) return of([]);
    return this._mongo.find<Group>(
      this._env.groupsListDB,
      this._env.groupListCollection,
      {
        _id: { $in: groupIds.map((id) => ({ $oid: id })) },
        is_archived: { $ne: true },
      },
    );
  }

  getGroupsWithPatientsByCreatorIds(
    creatorIds: string[],
  ): Observable<(Group & { patients: any[] })[]> {
    return this._mongo.aggregate<Group & { patients: any[] }>(
      this._env.groupsListDB,
      this._env.groupListCollection,
      [
        {
          $match: {
            creator_user_id: { $in: creatorIds },
            is_archived: { $ne: true },
          },
        },
        { $addFields: { group_id_string: { $toString: "$_id" } } },
        {
          $lookup: {
            from: this._env.userProfileCollection,
            let: { gid: "$group_id_string" },
            pipeline: [
              { $match: { $expr: { $eq: ["$assigned_group_id", "$$gid"] } } },
              { $project: { owner_user_id: 1, user_login_id: 1 } },
            ],
            as: "patients",
          },
        },
        { $project: { group_id_string: 0 } },
      ],
    );
  }

  getGroupsWithPatientsByTherapist(
    therapistOwnerUserId: string,
  ): Observable<(Group & { patients: any[] })[]> {
    return this._mongo.aggregate<Group & { patients: any[] }>(
      this._env.groupsListDB,
      this._env.groupListCollection,
      [
        {
          $match: {
            creator_user_id: therapistOwnerUserId,
            is_archived: { $ne: true },
          },
        },
        { $addFields: { group_id_string: { $toString: "$_id" } } },
        {
          $lookup: {
            from: this._env.userProfileCollection,
            let: { gid: "$group_id_string" },
            pipeline: [
              { $match: { $expr: { $eq: ["$assigned_group_id", "$$gid"] } } },
              { $project: { owner_user_id: 1, user_login_id: 1 } },
            ],
            as: "patients",
          },
        },
        { $project: { group_id_string: 0 } },
      ],
    );
  }

  getGroupByTherapistListNew(therapistID: string): Observable<Group[]> {
    return this._mongo.find<Group>(
      this._env.groupsListDB,
      this._env.groupListCollection,
      { creator_user_id: therapistID },
    );
  }

  createGroup(newGroup: Group): Observable<any> {
    return this._mongo.insertOne(
      this._env.groupsListDB,
      this._env.groupListCollection,
      newGroup,
    );
  }

  updateGroup(groupToUpdate: Group): Observable<any> {
    return this._mongo.updateOne(
      this._env.groupsListDB,
      this._env.groupListCollection,
      { _id: groupToUpdate._id },
      { $set: { ...groupToUpdate } },
    );
  }

  archiveGroup(objectId: any, performedByUserId?: string): Observable<any> {
    return this._mongo.updateOne(
      this._env.groupsListDB,
      this._env.groupListCollection,
      { _id: objectId },
      {
        $set: {
          is_archived: true,
          archived_date: new Date().toISOString(),
          ...(performedByUserId && { archived_by_user_id: performedByUserId }),
        },
      },
    );
  }

  deleteGroup(objectId: any): Observable<any> {
    return this._mongo.deleteOne(
      this._env.groupsListDB,
      this._env.groupListCollection,
      { _id: objectId },
    );
  }

  getGroup(groupID: string): Observable<Group> {
    if (!this._groupCache.has(groupID)) {
      const patientUserId = patientOfWebinarHolder(groupID);
      this._groupCache.set(
        groupID,
        (patientUserId
          ? this._mongo
              .findOne<PatientProfile>(
                this._env.userProfileDB,
                this._env.userProfileCollection,
                { owner_user_id: patientUserId },
              )
              .pipe(map((profile) => individualWebinarHolder(profile) as Group))
          : this._mongo.findOne<Group>(
              this._env.groupsListDB,
              this._env.groupListCollection,
              { _id: { $oid: groupID } },
            )
        ).pipe(shareReplay(1)),
      );
    }
    return this._groupCache.get(groupID)!;
  }

  clearGroupCache(groupID: string): void {
    this._groupCache.delete(groupID);
  }

  /**
   * Wipe every in-memory group cache this service maintains so the next
   * read of the same group / creator hits Mongo fresh. Used by the
   * webinar prepare resolver to guarantee the NGXS store is reloaded
   * with the repaired embedded webinar instead of the stale shareReplay
   * copy.
   */
  invalidateGroupCaches(groupID?: string): void {
    if (groupID) {
      this._groupCache.delete(groupID);
    } else {
      this._groupCache.clear();
    }
    this._groupsByCreatorCache.clear();
  }

  assignGroupToPatientList(
    groupID: string,
    ownerUserIdList: Array<string>,
    therapistId?: string,
    orgId?: string,
    performedByUserId?: string,
  ): Observable<any> {
    if (this._env.assignGroupToPatientsFunction && therapistId && orgId) {
      const endpoint = `${this._env.apiUrl}/api/functions/${this._env.assignGroupToPatientsFunction}`;
      return this._http.post(endpoint, {
        group_id: groupID,
        owner_user_ids: ownerUserIdList,
        therapist_id: therapistId,
        org_id: orgId,
        performed_by_user_id: performedByUserId,
      });
    }
    return this._mongo.updateMany(
      this._env.userProfileDB,
      this._env.userProfileCollection,
      { owner_user_id: { $in: [...ownerUserIdList] } },
      {
        $set: {
          assigned_group_id: groupID,
          assigned_to_group_date: new Date().toISOString(),
          personalised_course: null,
          ...(performedByUserId && {
            assigned_to_group_by_user_id: performedByUserId,
          }),
        },
        $unset: { individual_course: "" },
      },
    );
  }

  removeGroupFromPatientList(
    ownerUserIdList: string[],
    performedByUserId?: string,
  ): Observable<any> {
    return this._mongo.updateMany(
      this._env.userProfileDB,
      this._env.userProfileCollection,
      { owner_user_id: { $in: [...ownerUserIdList] } },
      {
        $set: {
          assigned_group_id: null,
          assigned_to_group_date: null,
          personalised_course: null,
          ...(performedByUserId && {
            removed_from_group_by_user_id: performedByUserId,
          }),
          removed_from_group_date: new Date().toISOString(),
        },
      },
    );
  }

  /**
   * The mongo passthrough returns HTTP 200 with { matchedCount } even when the filter
   * matched nothing (e.g. group/session not found, or a stale view). Without this guard
   * the caller's success handler fires and the UI toasts "locked" while nothing changed.
   * On matchedCount === 0 we raise a SessionUpdateNoMatchError so callers can surface a
   * "no update matched" warning, distinct from a real error.
   */
  private _failOnNoMatch(action: string) {
    return tap((result: { matchedCount?: number } | null) => {
      if (!result || result.matchedCount === 0) {
        throw new SessionUpdateNoMatchError(action);
      }
    });
  }

  manuallyLockSession(
    groupId: string,
    sessionId: string,
    performedByUserId?: string,
  ): Observable<any> {
    return this._mongo
      .updateOne(
        this._env.groupsListDB,
        this._env.groupListCollection,
        {
          _id: { $oid: groupId },
          "course.session_list._id": { $oid: sessionId },
        },
        {
          $set: {
            "course.session_list.$.is_unlocked": false,
            "course.session_list.$.is_manually_toggled": true,
            ...(performedByUserId && {
              "course.session_list.$.manual_toggled_by_user_id":
                performedByUserId,
            }),
            "course.session_list.$.manual_toggled_date":
              new Date().toISOString(),
          },
        },
      )
      .pipe(
        // i18n-ignore — SessionUpdateNoMatchError action label, not UI text
        this._failOnNoMatch("Session lock"),
        tap(() => this._groupCache.delete(groupId)),
      );
  }

  manuallyUnlockSession(
    groupId: string,
    sessionId: string,
    performedByUserId?: string,
  ): Observable<any> {
    return this._mongo
      .updateOne(
        this._env.groupsListDB,
        this._env.groupListCollection,
        {
          _id: { $oid: groupId },
          "course.session_list._id": { $oid: sessionId },
        },
        {
          $set: {
            "course.session_list.$.is_unlocked": true,
            "course.session_list.$.is_manually_toggled": true,
            ...(performedByUserId && {
              "course.session_list.$.manual_toggled_by_user_id":
                performedByUserId,
            }),
            "course.session_list.$.manual_toggled_date":
              new Date().toISOString(),
          },
        },
      )
      .pipe(
        // i18n-ignore — SessionUpdateNoMatchError action label, not UI text
        this._failOnNoMatch("Session unlock"),
        tap(() => this._groupCache.delete(groupId)),
      );
  }

  updateSessionAutomation(
    groupId: string,
    sessionId: string,
    automationConfig: SessionAutomationConfig,
  ): Observable<any> {
    return this._mongo.updateOne(
      this._env.groupsListDB,
      this._env.groupListCollection,
      {
        _id: { $oid: groupId },
        "course.session_list._id": { $oid: sessionId },
      },
      {
        $set: {
          "course.session_list.$.can_unlock_next_session_on_completion":
            automationConfig.canUnlockNextSession,
          "course.session_list.$.is_time_based_automation_enabled":
            automationConfig.isTimeBasedAutomationEnabled,
          "course.session_list.$.scheduled_unlock_date":
            automationConfig.scheduledUnlockDate,
          "course.session_list.$.scheduled_lock_date":
            automationConfig.scheduledLockDate,
          "course.session_list.$.unlock_sms_message":
            automationConfig.unlockSmsMessage,
          "course.session_list.$.lock_sms_message":
            automationConfig.lockSmsMessage,
        },
      },
    );
  }

  bulkEnableCompletionUnlocking(groupId: string): Observable<any> {
    return this._mongo.updateOne(
      this._env.groupsListDB,
      this._env.groupListCollection,
      { _id: { $oid: groupId } },
      {
        $set: {
          "course.session_list.$[].can_unlock_next_session_on_completion": true,
        },
      },
    );
  }

  getGroupsForTherapist(): Observable<Group[]> {
    if (!this._currentUserId) {
      return throwError(() => new Error("User not authenticated"));
    }

    return this._mongo.find<Group>(
      this._env.groupsListDB,
      this._env.groupListCollection,
      { creator_user_id: this._currentUserId, is_archived: { $ne: true } },
    );
  }

  updateSessionSchedule(
    groupId: string,
    sessionId: string,
    config: any,
  ): Observable<any> {
    const updateFields: any = {};

    if (config.scheduled_unlock_date !== undefined) {
      updateFields["course.session_list.$.scheduled_unlock_date"] =
        config.scheduled_unlock_date;
    }
    if (config.scheduled_lock_date !== undefined) {
      updateFields["course.session_list.$.scheduled_lock_date"] =
        config.scheduled_lock_date;
    }
    if (config.unlock_sms_message !== undefined) {
      updateFields["course.session_list.$.unlock_sms_message"] =
        config.unlock_sms_message;
    }
    if (config.lock_sms_message !== undefined) {
      updateFields["course.session_list.$.lock_sms_message"] =
        config.lock_sms_message;
    }

    if (config.scheduled_unlock_date === null) {
      updateFields["course.session_list.$.unlocked_by_schedule_date"] = null;
    }
    if (config.scheduled_lock_date === null) {
      updateFields["course.session_list.$.locked_by_schedule_date"] = null;
    }

    return this._mongo.updateOne(
      this._env.groupsListDB,
      this._env.groupListCollection,
      {
        _id: { $oid: groupId },
        "course.session_list._id": { $oid: sessionId },
      },
      { $set: updateFields },
    );
  }
}
